/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { ConfirmPhoneNumberComponent } from 'assets/components';
import Countdown from '../../../utilities/counter';
import { sharedSetting } from '../../../utilities/constants';
import { PARTIAL_SIGN_UP, PHONE_VERIFICATION_CHECK, PHONE_VERIFICATION_SEND } from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import {
  MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_CODE,
  MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_RESEND,
} from '../mixpanel-events';
import { PARTIAL_SIGN_UP_PHASES, RECAPTCHA_EVENTS } from '../constants';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import RecaptchaModal from '../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../utilities/caching';

const phoneCountdown = new Countdown();

const ConfirmPhoneNumber = () => {
  const { t: translate } = useTranslation();

  const { state } = useLocation();
  const history = useHistory();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const [isSubmitRequest, setIsSubmitRequest] = useState(false);

  const { state: onboardingState } = useContext(OnboardingContext);
  const allowPartialOnboarding = onboardingState?.mainSettings?.feature_enable_partial_registration;

  const [form, setForm] = useState({
    confirmed_phone_number: `${state?.country_code} ${state?.phone_number}`,
    code: '',
    via: sharedSetting.MODES_2FA.sms,
    ...state,
  });

  const [timer, setTimer] = useState();

  useEffect(() => {
    if (!state?.country_code || !state?.phone_number) {
      history.push('/onboarding/signup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    phoneCountdown.bind(setTimer);
    phoneCountdown.start();
    return () => {
      phoneCountdown.stop();
    };
  }, []);

  const { mutate: partialSignup } = useMutation(PARTIAL_SIGN_UP, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        history.push('personal-information', { ...form, ...state, partialSignupData: data });
      }
    },
  });

  const { mutate: checkPhoneVerification, isLoading } = useMutation(PHONE_VERIFICATION_CHECK, {
    onSettled: (data, error) => {
      sendStats(MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_CODE, { error }, FLOWS.ONBOARDING);
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else if (allowPartialOnboarding) {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        partialSignup({
          id: state?.partialSignupData?.id,
          phase: PARTIAL_SIGN_UP_PHASES.PHONE_NUMBER,
          country_code: state.country_code,
          phone_number: state.phone_number,
          verification_id: state?.verification_id,
        });
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        history.push('personal-information', {
          ...state,
          ...form,
        });
      }
    },
  });

  const { mutate: requestPhoneVerification } = useMutation(PHONE_VERIFICATION_SEND, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        phoneCountdown.start();
      }
      sendStats(MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_RESEND, { error }, FLOWS.ONBOARDING);
    },
  });

  const handleResendOnClick = async (e) => {
    e.preventDefault();

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      requestPhoneVerification({
        ...state,
        verificationId: state?.verification_id,
        isSignUp: true,
        token,
      });
    } catch (error) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitRequest(true);

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_CHECK_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      const params = {
        country_code: state?.country_code,
        phone_number: state?.phone_number,
        verification_code: form.code,
        verification_id: state?.verification_id,
        token,
      };

      checkPhoneVerification(params);
    } catch (error) {
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="confirm-phone-form"
          loading={isLoading ? 'true' : 'false'}
          disabled={!form.code}>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="confirm-phone-form"
          loading={isLoading ? 'true' : 'false'}
          disabled={!form.code}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    if (isSubmitRequest) {
      const params = {
        country_code: state?.country_code,
        phone_number: state?.phone_number,
        verification_code: form.code,
        verification_id: state?.verification_id,
        token: tokenV2,
      };

      checkPhoneVerification(params);
    } else {
      requestPhoneVerification({
        ...state,
        verificationId: state?.verification_id,
        token: tokenV2,
        isSignUp: true,
      });
    }
  };

  return (
    <>
      <ConfirmPhoneNumberComponent
        form={form}
        state={state}
        timer={timer}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        handleResendOnClick={handleResendOnClick}
        renderButtons={renderButtons}
        isLoading={isLoading}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoading}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default ConfirmPhoneNumber;
